/**
 * @file psd/utils.ts
 * @description Utilities and mappings for PSD handling.
 */

import { BlendMode } from '@hcie/core';
import { PSDNode } from './types';

export const PSD_BLEND_MAP: Record<string, BlendMode> = {
  'norm': 'source-over', 'mul ': 'multiply', 'scrn': 'screen',
  'over': 'overlay', 'dark': 'darken', 'lite': 'lighten',
  'diff': 'difference', 'color': 'color', 'lum ': 'luminosity',
  'hue ': 'hue', 'sat ': 'saturation',
};

export const REVERSE_BLEND_MAP: Record<string, string> = {
  'source-over': 'normal',
  'multiply': 'multiply',
  'screen': 'screen',
  'overlay': 'overlay',
  'darken': 'darken',
  'lighten': 'lighten',
  'color-dodge': 'color-dodge',
  'color-burn': 'color-burn',
  'hard-light': 'hard-light',
  'soft-light': 'soft-light',
  'difference': 'difference',
  'exclusion': 'exclusion',
  'hue': 'hue',
  'saturation': 'saturation',
  'color': 'color',
  'luminosity': 'luminosity',
};

export function getCleanName(node: PSDNode): string {
  const layer = (node as any).layer;
  let rawName = node.name || layer?.name || 'Layer';

  if (layer?.additionalData?.luni) {
    rawName = layer.additionalData.luni;
  } else if (layer?.additionalData?.['nam ']) {
    rawName = layer.additionalData['nam '];
  }

  if (!rawName) return 'Layer';

  let clean = String(rawName)
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '')
    .replace(/8BIM.*/g, '')
    .replace(/unim.*/g, '')
    .trim();

  if (!clean || clean.length < 1) {
    const match = String(rawName).match(/^[a-zA-Z0-0\s_\-]+/);
    clean = match ? match[0].trim() : 'Layer';
  }

  return clean || 'Layer';
}

/**
 * Ensures the given canvas-like object is an HTMLCanvasElement.
 */
export function ensureHTMLCanvas(canvas: any): HTMLCanvasElement | null {
  if (!canvas) return null;
  if (canvas instanceof HTMLCanvasElement) return canvas;
  
  if (typeof HTMLCanvasElement === 'undefined') {
    return canvas; 
  }

  const result = document.createElement('canvas');
  result.width = canvas.width;
  result.height = canvas.height;
  const ctx = result.getContext('2d');
  if (ctx) {
      if (canvas instanceof ImageData) {
          ctx.putImageData(canvas, 0, 0);
      } else {
        ctx.drawImage(canvas, 0, 0);
      }
  }
  return result;
}
